import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  TextField,
  InputAdornment,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Chip,
  Avatar,
  IconButton,
  Button,
  Paper,
  Fade,
  Divider,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import { Opinion } from '../../interface/Opinion';
import { getOpinions } from '../../services/OpinionService';
import { formatDistanceToNow } from 'date-fns';
import UserAvatar from '../common/UserAvatar';

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [opinions, setOpinions] = useState<Opinion[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  const loadOpinions = useCallback(async (pageNum: number, isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await getOpinions(pageNum, 20);

      // Only public opinions
      const publicOpinions = response.opinions.filter((op) => op.isPublic === true);

      if (isInitial) {
        setOpinions(publicOpinions);
      } else {
        setOpinions((prev) => [...prev, ...publicOpinions]);
      }

      // Keep loading pages until we've reached the end of all opinions
      setHasMore(pageNum < response.pagination.totalPages);
    } catch (error) {
      console.error('Error loading opinions:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOpinions(nextPage, false);
    }
  }, [page, loadingMore, hasMore, searchQuery, loadOpinions]);

  // Load opinions when search query changes (or on initial mount)
  useEffect(() => {
    setOpinions([]);
    setPage(1);
    setHasMore(true);
    loadOpinions(1, true);
  }, [searchQuery, loadOpinions]);

  // Update URL when search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery });
    } else {
      setSearchParams({});
    }
  }, [searchQuery, setSearchParams]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && searchQuery.trim()) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, searchQuery, loadMore]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleOpinionClick = (opinionId: string) => {
    navigate(`/opinions/${opinionId}`);
  };

  const totalVotes = (opinion: Opinion) => (opinion.agreeCount || 0) + (opinion.disagreeCount || 0);
  const agreePercentage = (opinion: Opinion) => {
    const total = totalVotes(opinion);
    return total > 0 ? Math.round((opinion.agreeCount / total) * 100) : 0;
  };

  return (
    <>
      <Helmet>
        <title>{searchQuery ? `Search: ${searchQuery} | OpinLoop` : 'Search Public Opinions | OpinLoop'}</title>
        <meta name="description" content="Search and discover public opinions on OpinLoop. Find discussions on topics that interest you." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://opinloop.com/search${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`} />
      </Helmet>

      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)' }}>
        {/* Hero Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            position: 'relative',
            overflow: 'hidden',
            pb: { xs: 3, md: 4 },
            pt: { xs: 3, md: 4 },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80") center/cover',
              opacity: 0.1,
              animation: 'zoomIn 20s ease-in-out infinite alternate'
            }
          }}
        >
          <style>
            {`
              @keyframes zoomIn {
                from { transform: scale(1); }
                to { transform: scale(1.1); }
              }
              @keyframes fadeInScale {
                from {
                  opacity: 0;
                  transform: scale(0.9);
                }
                to {
                  opacity: 1;
                  transform: scale(1);
                }
              }
            `}
          </style>

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 1.5, sm: 2, md: 3 } }}>
            <Fade in timeout={600}>
              <Box sx={{ textAlign: 'center', color: 'white', mb: 3 }}>
                {/* Logo */}
                <Box
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    display: 'flex',
                    justifyContent: 'center',
                    animation: 'fadeInScale 0.8s ease-out'
                  }}
                >
                  <Box
                    component="img"
                    src="/images/logo.png"
                    alt="OpinLoop"
                    onClick={() => navigate('/')}
                    sx={{
                      height: { xs: 60, sm: 80, md: 100 },
                      width: 'auto',
                      objectFit: 'contain',
                      borderRadius: 3,
                      cursor: 'pointer',
                      filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                  }}
                >
                  Search Public Opinions
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.95,
                    maxWidth: 600,
                    mx: 'auto',
                    mb: 3,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    px: { xs: 2, sm: 0 }
                  }}
                >
                  Discover what people are thinking about. Search through public opinions and join the conversation.
                </Typography>

                {/* Search Bar */}
                <Paper
                  elevation={4}
                  sx={{
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 3,
                    maxWidth: 700,
                    mx: 'auto',
                    bgcolor: 'white'
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Search by content, author, category, or topic..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 24 } }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClearSearch}
                            size="small"
                            sx={{ minWidth: 32, minHeight: 32 }}
                          >
                            <ClearIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        '& fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  />
                </Paper>
              </Box>
            </Fade>
          </Container>
        </Box>

        {/* Results Section */}
        <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
          {loading && opinions.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress size={48} sx={{ color: '#667eea' }} />
            </Box>
          ) : opinions.length === 0 ? (
            <Fade in timeout={800}>
              <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 }, textAlign: 'center' }}>
                  <SearchIcon sx={{ fontSize: { xs: 64, sm: 80 }, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h5" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    No Opinions Found
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, maxWidth: 500, mx: 'auto', mb: 2 }}>
                    {searchQuery
                      ? `We couldn't find any public opinions matching "${searchQuery}". Try different keywords or clear the search.`
                      : 'No public opinions are available yet.'}
                  </Typography>
                  {searchQuery && (
                    <Button
                      variant="outlined"
                      onClick={handleClearSearch}
                      sx={{
                        minWidth: { xs: 120, sm: 140 },
                        minHeight: { xs: 36, sm: 40 },
                        touchAction: 'manipulation'
                      }}
                    >
                      Clear Search
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Fade>
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, mb: 0.5 }}>
                  {searchQuery ? 'Search Results' : 'All Public Opinions'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                  {searchQuery
                    ? `Found ${opinions.length} ${opinions.length === 1 ? 'opinion' : 'opinions'} matching "${searchQuery}"`
                    : `Showing ${opinions.length} public ${opinions.length === 1 ? 'opinion' : 'opinions'} (scroll to load more)`}
                </Typography>
              </Box>

              <Stack spacing={{ xs: 1.5, sm: 2 }}>
                {opinions.map((opinion) => (
                  <Fade in key={opinion._id} timeout={600}>
                    <Card
                      onClick={() => handleOpinionClick(opinion._id)}
                      sx={{
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        borderRadius: 3,
                        boxShadow: 2,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4
                        },
                        touchAction: 'manipulation'
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                        {/* Header */}
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={{ xs: 1.5, md: 2 }} flexWrap="wrap" gap={1}>
                          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }} flexWrap="wrap">
                            <UserAvatar
                              profileIcon={undefined}
                              username={opinion.username}
                              size={40}
                              sx={{ bgcolor: 'primary.main' }}
                            />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                                <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                                  {opinion.username}
                                </Typography>
                                {opinion.category && (
                                  <Chip
                                    label={opinion.category}
                                    size="small"
                                    sx={{
                                      bgcolor: 'primary.light',
                                      color: 'primary.dark',
                                      height: { xs: 20, sm: 22 },
                                      fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                                {opinion.isPublic && (
                                  <Chip
                                    icon={<PublicIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                                    label="Public"
                                    size="small"
                                    sx={{
                                      bgcolor: 'info.light',
                                      color: 'info.dark',
                                      height: { xs: 20, sm: 22 },
                                      fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                                {formatDistanceToNow(new Date(opinion.createdAt), { addSuffix: true })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Content */}
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.7,
                            mb: { xs: 1.5, md: 2 },
                            color: 'text.primary',
                            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {opinion.content}
                        </Typography>

                        {/* Target Audience */}
                        {opinion.targetAudience && opinion.targetAudience.length > 0 && (
                          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mb={{ xs: 1.5, md: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem' } }}>
                              Seeking:
                            </Typography>
                            {opinion.targetAudience.slice(0, 3).map((audience, idx) => (
                              <Chip
                                key={idx}
                                label={audience}
                                size="small"
                                sx={{
                                  height: { xs: 18, sm: 20 },
                                  fontSize: { xs: '0.625rem', sm: '0.6875rem' },
                                  bgcolor: 'secondary.light',
                                  color: 'secondary.dark'
                                }}
                              />
                            ))}
                            {opinion.targetAudience.length > 3 && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem' } }}>
                                +{opinion.targetAudience.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Divider sx={{ my: { xs: 1.5, md: 2 } }} />

                        {/* Stats */}
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                          <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }} flexWrap="wrap">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ThumbUpIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'success.main' }} />
                              <Typography variant="body2" color="success.main" fontWeight={600} sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                                {opinion.agreeCount || 0}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ThumbDownIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'error.main' }} />
                              <Typography variant="body2" color="error.main" fontWeight={600} sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                                {opinion.disagreeCount || 0}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CommentIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'primary.main' }} />
                              <Typography variant="body2" color="primary.main" fontWeight={600} sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                                {opinion.commentCount || 0}
                              </Typography>
                            </Box>
                          </Box>

                          {totalVotes(opinion) > 10 && (
                            <Chip
                              icon={<TrendingUpIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                              label="Trending"
                              size="small"
                              sx={{
                                bgcolor: 'warning.light',
                                color: 'warning.dark',
                                height: { xs: 22, sm: 24 },
                                fontSize: { xs: '0.6875rem', sm: '0.75rem' }
                              }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                ))}

                {/* Infinite Scroll Trigger */}
                {hasMore && (
                  <Box
                    ref={observerTarget}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 3,
                      minHeight: 60
                    }}
                  >
                    {loadingMore && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                          Loading more opinions...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {!hasMore && opinions.length > 0 && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, fontStyle: 'italic' }}>
                      You've reached the end of the results
                    </Typography>
                  </Box>
                )}
              </Stack>
            </>
          )}
        </Container>
      </Box>
    </>
  );
};

export default SearchPage;
